import { ChangeDetectorRef, Component, DestroyRef, inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { RouterLink } from '@angular/router';
import { AsyncPipe, LowerCasePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { take, timer } from 'rxjs';
import { tap } from 'rxjs/operators';

import { sortBy } from 'lodash-es';

import { GanLucidIcon } from '@gan/shared/ui-icon';

import {
  NotificationDuration,
  OpsNotificationModel,
  OpsNotificationsService,
  OpsNotificationsSignalRServiceService,
} from '@gan/common/data-ops-notification';

import { SignalREventModel, SignalRStateEnum } from '@gan/core/util-signalr';
import { LanguageService } from '@gan/core/feature-language';

@Component({
  selector: 'gan-ops-notifications',
  templateUrl: './ops-notifications.component.html',
  styleUrls: ['./ops-notifications.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [RouterLink, MatIconButton, GanLucidIcon, LowerCasePipe, AsyncPipe]
})
export class OpsNotificationsComponent implements OnDestroy {
  readonly languageService = inject(LanguageService);
  private cdr = inject(ChangeDetectorRef);
  private opsNotificationsService = inject(OpsNotificationsService);
  private opsNotificationsSignalRServiceService = inject(OpsNotificationsSignalRServiceService);
  private destroyRef = inject(DestroyRef);

  notifications: OpsNotificationModel[] = [];

  constructor() {
    this.initOpsNotifications();
  }

  ngOnDestroy(): void {
    this.opsNotificationsSignalRServiceService.disconnectHub();
  }

  private initOpsNotifications() {
    this.opsNotificationsService
      .getOpsNotifications()
      .pipe(
        tap((resp: OpsNotificationModel[]) => {
          this.notifications = sortBy(resp, 'createdTime');

          this.collapseAllNotifications();

          this.opsNotificationsSignalRServiceService.startHubConnection().then(() => {
            this.subscribeOnOpsNotifications();
          });
        })
      )
      .subscribe();
  }

  private subscribeOnOpsNotifications(): void {
    this.opsNotificationsSignalRServiceService.hubEvent
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((response: SignalREventModel<OpsNotificationModel>[]) => {
          response.forEach(notification => {
            const index = this.notifications.findIndex(message => message.id === notification.key);

            switch (notification.state) {
              case SignalRStateEnum.MODIFIED:
                if (index > -1) {
                  this.notifications[index] = notification.value;
                } else {
                  this.notifications = sortBy([...this.notifications, notification.value], 'createdTime');
                }

                break;
              case SignalRStateEnum.REMOVED:
                if (index > -1) {
                  this.notifications.splice(index, 1);
                }

                break;
            }
          });

          this.collapseAllNotifications();

          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  private collapseAllNotifications() {
    timer(NotificationDuration.DEFAULT)
      .pipe(take(1))
      .subscribe(() => {
        this.notifications.forEach(notification => {
          if (!notification.collapsed) {
            notification.collapsed = !notification.collapsed;
          }
        });
      });
  }
}
